import { useState, useEffect } from "react";
import { Award, CreditCard, TrendingUp, Gift, LogIn, Clock, Plus, Minus } from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import { useAppContext } from "../../../context";
import { Link } from "react-router-dom";

const VirtualCard = ({ card, userName }) => {
  if (!card) return null;
  const active = card.status === "active";
  return (
    <div className={`relative rounded-2xl p-6 text-white overflow-hidden select-none ${
      active
        ? "bg-gradient-to-br from-orange-500 via-orange-400 to-red-500 shadow-xl shadow-orange-200"
        : "bg-gradient-to-br from-gray-400 to-gray-500 shadow-md"
    }`}>
      <div className="absolute -top-8 -right-8 w-36 h-36 bg-white/10 rounded-full" />
      <div className="absolute -bottom-10 -left-5 w-44 h-44 bg-white/10 rounded-full" />
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-8">
          <div>
            <p className="text-[10px] font-bold opacity-70 uppercase tracking-widest">Gamapa Loyalty</p>
            <p className="text-base font-extrabold mt-0.5">Virtual Card</p>
          </div>
          <span className={`text-[10px] font-black px-3 py-1 rounded-full ${active ? "bg-white/20" : "bg-white/10"}`}>
            {active ? "ACTIVE" : "BLOCKED"}
          </span>
        </div>
        <p className="font-mono text-xl tracking-widest font-bold mb-8">{card.card_number}</p>
        <div className="flex justify-between items-end">
          <div>
            <p className="text-[10px] opacity-60 uppercase">Card Holder</p>
            <p className="text-sm font-bold">{userName}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] opacity-60 uppercase">Member Since</p>
            <p className="text-sm font-bold">{card.created_at ? new Date(card.created_at).getFullYear() : new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const TxRow = ({ t }) => {
  const positive = Number(t.points) >= 0;
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${positive ? "bg-green-100" : "bg-red-100"}`}>
          {positive ? <Plus size={14} className="text-green-600" /> : <Minus size={14} className="text-red-500" />}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{t.description || t.type}</p>
          <p className="text-xs text-gray-400">{new Date(t.created_at).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}</p>
        </div>
      </div>
      <span className={`text-base font-extrabold ${positive ? "text-green-600" : "text-red-500"}`}>
        {positive ? "+" : ""}{t.points}
      </span>
    </div>
  );
};

const LoyaltyPage = () => {
  const { customerUser } = useAppContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerUser?.id) { setLoading(false); return; }
    axios.get(`${apiUrl}/loyalty?user_id=${customerUser.id}`)
      .then((res) => setData(res.data?.status ? res.data : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [customerUser]);

  if (!customerUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Award size={30} className="text-orange-500" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Loyalty Rewards</h2>
          <p className="text-gray-500 mb-6">Sign in to view your points and virtual loyalty card.</p>
          <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition">
            <LogIn size={18} /> Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400">Loading your loyalty card...</p>
      </div>
    );
  }

  const balance = data?.balance ?? 0;
  const card = data?.card;
  const transactions = data?.transactions || [];

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto space-y-6">

        {/* Page title */}
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900">My Loyalty</h1>
          <p className="text-gray-500 mt-1">Earn points on every order and redeem them for discounts</p>
        </div>

        {/* Virtual card */}
        <VirtualCard card={card} userName={`${customerUser.firstName || ""} ${customerUser.lastName || ""}`} />

        {/* Points balance */}
        <div className="bg-white rounded-2xl border p-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Points</p>
            <p className="text-5xl font-extrabold text-orange-500 mt-1">{balance}</p>
            <p className="text-xs text-gray-400 mt-1">1 point = $0.01 discount</p>
          </div>
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center">
            <Award size={32} className="text-orange-500" />
          </div>
        </div>

        {/* How to earn */}
        <div className="bg-orange-50 rounded-2xl p-5">
          <p className="font-bold text-orange-800 mb-3">How to earn points</p>
          <div className="space-y-2 text-sm text-orange-700">
            <div className="flex items-center gap-2"><TrendingUp size={15} /> Earn 1 point per $1 spent on any order</div>
            <div className="flex items-center gap-2"><Gift size={15} /> Bonus points on special promotions</div>
            <div className="flex items-center gap-2"><CreditCard size={15} /> Redeem at checkout for a discount</div>
          </div>
        </div>

        {/* Transaction history */}
        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center gap-2">
            <Clock size={16} className="text-gray-400" />
            <h2 className="font-bold text-gray-900">Points History</h2>
          </div>
          {transactions.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-400 text-sm">
              No transactions yet. Place your first order to start earning!
            </div>
          ) : (
            <div className="px-5 divide-y">
              {transactions.map((t) => <TxRow key={t.id} t={t} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoyaltyPage;
